import { Grupo } from "@apiUser";
import { Users, Building2, MapPin } from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  fmt,
  ScrollArea,
} from "visuais";

interface GroupsAccessCardProps {
  grupos: Grupo[];
}

/**
 * Card que exibe os grupos e filiais de acesso de um usuário em uma estrutura de acordeão.
 * @param {GroupsAccessCardProps} props - As propriedades do componente.
 * @returns {JSX.Element} Um card com um acordeão para visualizar a hierarquia de grupos.
 */
export function GroupsAccessCard({ grupos }: GroupsAccessCardProps) {
  // Validação inicial
  if (!grupos || grupos.length === 0) {
    return (
      <Card className="flex w-full flex-col h-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-foreground">
            <Users className="w-5 h-5" />
            Grupos e Filiais
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Nenhuma informação de grupo ou filial disponível.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  // Debug para verificar a estrutura dos dados
  console.log("🔍 [GroupsAccessCard] Dados recebidos:", grupos);

  // Filtra grupos válidos com empresas
  const gruposValidos = grupos.filter((grupo) => {
    const isValid = grupo && grupo.empresas && Array.isArray(grupo.empresas);
    if (!isValid) {
      console.warn("⚠️ [GroupsAccessCard] Grupo inválido encontrado:", grupo);
    }
    return isValid;
  });

  if (gruposValidos.length === 0) {
    return (
      <Card className="flex w-full flex-col h-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-foreground">
            <Users className="w-5 h-5" />
            Grupos e Filiais
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Estrutura de grupos inválida ou incompleta.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">
            <p>Problemas detectados:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              {grupos.map((grupo, index) => (
                <li key={index}>
                  Grupo {index + 1}:{" "}
                  {!grupo
                    ? "Objeto nulo"
                    : !grupo.empresas
                    ? "Sem propriedade 'empresas'"
                    : !Array.isArray(grupo.empresas)
                    ? "Empresas não é um array"
                    : "OK"}
                </li>
              ))}
            </ul>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="flex w-full flex-col h-full pb-0 shadow-lg shadow-primary/15">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-foreground">
          <Users className="w-5 h-5" />
          Grupos e Filiais
        </CardTitle>
        <CardDescription className="text-muted-foreground">
          Estrutura organizacional e filiais com acesso liberado.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex-1 p-0 overflow-hidden">
        <ScrollArea className="h-full px-6 py-4">
          <Accordion type="single" collapsible className="w-full">
            {gruposValidos.map((grupo) => (
              <AccordionItem
                key={grupo.codigo}
                value={`grupo-${grupo.codigo}`}
                className="border-border px-1"
              >
                <AccordionTrigger
                  className="[&[data-state=open]]:bg-primary [&[data-state=open]]:text-white"
                  iconClassName="[&[data-state=open]]:text-white"
                >
                  <h3 className="font-semibold">{grupo.descricao}</h3>
                  <div className="flex items-center gap-2 ml-auto">
                    {grupo.empresas && grupo.empresas.length > 0 ? (
                      grupo.empresas.map((empresa) => (
                        <Badge
                          key={empresa?.codigo || "sem-codigo"}
                          variant="secondary"
                          className="text-xs font-medium"
                        >
                          <Building2 className="w-3 h-3 mr-1 text-secondary-foreground" />
                          Empresa: {empresa?.codigo || "N/A"}
                        </Badge>
                      ))
                    ) : (
                      <Badge variant="outline" className="text-xs">
                        Sem empresas
                      </Badge>
                    )}
                  </div>
                </AccordionTrigger>

                <AccordionContent className="py-2 pl-6">
                  <div className="space-y-4 pr-6">
                    {grupo.empresas && grupo.empresas.length > 0 ? (
                      grupo.empresas.map((empresa) => {
                        // Validação da empresa
                        if (!empresa) {
                          console.warn(
                            "⚠️ [GroupsAccessCard] Empresa nula encontrada"
                          );
                          return null;
                        }

                        const filiais = empresa.filiais || [];

                        return (
                          <div
                            key={empresa.codigo || "empresa-sem-codigo"}
                            className="space-y-3"
                          >
                            {filiais.length > 0 ? (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {filiais.map((filial, index) => {
                                  // Validação da filial
                                  if (!filial) {
                                    console.warn(
                                      "⚠️ [GroupsAccessCard] Filial nula encontrada"
                                    );
                                    return null;
                                  }

                                  return (
                                    <Card
                                      key={filial.codigo || `filial-${index}`}
                                      className=""
                                    >
                                      <div className="flex flex-col items-center justify-center gap-1 text-center p-4">
                                        <MapPin className="size-5" />
                                        <span className="font-semibold text-sm text-card-foreground">
                                          {filial.nome || "Nome não disponível"}
                                        </span>
                                        <p className="text-xs text-muted-foreground">
                                          CNPJ:{" "}
                                          {filial.cgc
                                            ? fmt.strings.cnpj(filial.cgc)
                                            : "N/A"}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                          Código: {filial.codigo || "N/A"}
                                        </p>
                                      </div>
                                    </Card>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="text-center py-4 text-muted-foreground">
                                <p className="text-sm">
                                  Nenhuma filial encontrada para a empresa{" "}
                                  {empresa.codigo}
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-4 text-muted-foreground">
                        <p className="text-sm">
                          Nenhuma empresa encontrada neste grupo
                        </p>
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
